import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingCart, LogOut, User, Package, LayoutGrid, Menu, X } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const cart = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAuth = !!user && user !== false;
  const isAdmin = isAuth && user.role === "admin";
  const isApproved = isAuth && (user.role === "admin" || user.status === "approved");

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const closeMenu = () => setOpen(false);

  const navLinkCls = ({ isActive }) =>
    isActive ? "text-[#2D6A4F]" : "hover:text-[#2D6A4F]";

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl backdrop-saturate-150 bg-white/85 border-b border-[#E5E3DC]"
      data-testid="site-header"
    >
      <div className="ff-container flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-home-link" onClick={closeMenu} className="flex items-center gap-3">
          <img src={LOGO_URL} alt="FineFood Primeur" className="h-12 w-auto" />
          <span className="hidden sm:block font-serif text-xl text-[#1A2B1E] tracking-tight">
            FineFood <span className="text-[#E76F51]">Primeur</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#4A5D4E]">
          <NavLink to="/" end data-testid="nav-home" className={navLinkCls}>
            Accueil
          </NavLink>
          {/* Shop always visible — ProtectedRoute redirects to /login if needed */}
          <NavLink to="/shop" data-testid="nav-shop" className={navLinkCls}>
            Shop
          </NavLink>
          {isAuth && !isAdmin && (
            <NavLink to="/orders" data-testid="nav-orders" className={navLinkCls}>
              Mes commandes
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/admin" data-testid="nav-admin" className={navLinkCls}>
              <LayoutGrid className="inline w-4 h-4 mr-1" /> Admin
            </NavLink>
          )}
        </nav>

        {/* Right side actions */}
        <div className="flex items-center gap-2 md:gap-3">
          {isApproved && !isAdmin && (
            <Link
              to="/cart"
              data-testid="cart-button"
              className="relative p-2 hover:text-[#2D6A4F] transition-colors"
              aria-label="Panier"
            >
              <ShoppingCart className="w-5 h-5" />
              {cart.count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-1 -right-1 bg-[#E76F51] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
                >
                  {Math.round(cart.count)}
                </span>
              )}
            </Link>
          )}

          {/* Desktop auth actions */}
          {isAuth ? (
            <div className="hidden md:flex items-center gap-2">
              <span className="hidden lg:inline text-sm text-[#4A5D4E]" data-testid="user-email">
                <User className="inline w-4 h-4 mr-1" />
                {user.company_name || user.email}
              </span>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-sm font-medium text-[#4A5D4E] hover:text-[#D62828] flex items-center gap-1.5 px-3 py-2"
              >
                <LogOut className="w-4 h-4" /> Déconnexion
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-1">
              <Link to="/login" data-testid="header-login-link" className="text-sm font-medium text-[#2D6A4F] hover:text-[#1B4332] px-4 py-2">
                Connexion
              </Link>
              <Link to="/register" data-testid="header-register-link" className="ff-btn-primary text-sm">
                <Package className="w-4 h-4" /> Compte pro
              </Link>
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="md:hidden p-2 text-[#1A2B1E] hover:text-[#2D6A4F]"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {open && (
        <div className="md:hidden bg-white border-t border-[#E5E3DC] shadow-lg" data-testid="mobile-menu">
          <nav className="ff-container py-4 flex flex-col gap-1 text-[#1A2B1E]">
            <Link to="/" onClick={closeMenu} className="py-3 border-b border-[#F2EFEB]" data-testid="mobile-nav-home">Accueil</Link>
            <Link to="/shop" onClick={closeMenu} className="py-3 border-b border-[#F2EFEB]" data-testid="mobile-nav-shop">Shop</Link>
            {isAuth && !isAdmin && (
              <Link to="/orders" onClick={closeMenu} className="py-3 border-b border-[#F2EFEB]" data-testid="mobile-nav-orders">Mes commandes</Link>
            )}
            {isAdmin && (
              <Link to="/admin" onClick={closeMenu} className="py-3 border-b border-[#F2EFEB] flex items-center gap-2" data-testid="mobile-nav-admin">
                <LayoutGrid className="w-4 h-4" /> Espace admin
              </Link>
            )}
            {isAuth ? (
              <>
                <div className="py-3 text-sm text-[#8A9A8E]" data-testid="mobile-user-info">
                  Connecté : {user.company_name || user.email}
                </div>
                <button
                  onClick={handleLogout}
                  className="ff-btn-secondary justify-center mt-2"
                  data-testid="mobile-logout"
                >
                  <LogOut className="w-4 h-4" /> Déconnexion
                </button>
              </>
            ) : (
              <div className="flex gap-2 mt-3">
                <Link to="/login" onClick={closeMenu} className="ff-btn-secondary flex-1 justify-center" data-testid="mobile-login">
                  Connexion
                </Link>
                <Link to="/register" onClick={closeMenu} className="ff-btn-primary flex-1 justify-center" data-testid="mobile-register">
                  Compte pro
                </Link>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
